import { ALLOWED_IMAGE_MIME_TYPES, creatorBadge } from "@coinfallsmp/shared";
import { useState } from "react";
import { useUploadsEnabled } from "../AppConfigContext.js";
import { creatorOf, useAuth } from "../AuthContext.js";
import {
  changeMyPassword,
  finalizeMyAvatar,
  getMyAvatarUploadUrl,
  updateMe,
  uploadToPresignedUrl,
} from "../api.js";
import CreatorBadge from "../components/CreatorBadge.js";

export default function MyProfile() {
  const { identity, loading, refresh } = useAuth();
  const uploadsEnabled = useUploadsEnabled();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  if (loading) return <p className="status-message">Loading…</p>;
  const creator = creatorOf(identity);
  if (!creator) {
    return <p className="status-message">Only creator accounts have a profile to edit.</p>;
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await updateMe({
        displayName: String(form.get("displayName") ?? ""),
        bio: String(form.get("bio") ?? "") || null,
        avatarUrl: String(form.get("avatarUrl") ?? "").trim() || null,
      });
      refresh();
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  async function onAvatarChange(file: File | null) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const { uploadUrl, key } = await getMyAvatarUploadUrl({
        contentType: file.type as (typeof ALLOWED_IMAGE_MIME_TYPES)[number],
        fileSizeBytes: file.size,
      });
      await uploadToPresignedUrl(uploadUrl, file);
      await finalizeMyAvatar(key);
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Avatar upload failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="form-page">
      <h1>
        <CreatorBadge badge={creatorBadge(creator)} /> My Profile
      </h1>
      {creator.mustResetPassword ? (
        <p className="form-error">
          You're still using a temporary password — set a new one below before continuing.
        </p>
      ) : null}
      <p className="form-hint">
        @{creator.username} — public at{" "}
        <a href={`https://media.coinfallsmp.net/creators/${creator.username}`} target="_blank" rel="noreferrer">
          /creators/{creator.username}
        </a>
        . Username, email, and verification status are set by an admin.
      </p>

      <form onSubmit={onSubmit}>
        <label>
          Display name
          <input name="displayName" defaultValue={creator.displayName} required />
        </label>
        <label>
          Bio
          <textarea name="bio" rows={5} defaultValue={creator.bio ?? ""} />
        </label>
        <label>
          Avatar image URL
          <input
            name="avatarUrl"
            type="url"
            defaultValue={creator.avatarUrl ?? ""}
            placeholder="https://cdn.discordapp.com/avatars/…"
            disabled={busy}
          />
        </label>
        {creator.avatarUrl ? (
          <img
            src={creator.avatarUrl}
            alt=""
            className="avatar-preview"
            referrerPolicy="no-referrer"
          />
        ) : null}
        {uploadsEnabled ? (
          <label>
            Or upload a file
            <input
              type="file"
              accept={ALLOWED_IMAGE_MIME_TYPES.join(",")}
              onChange={(e) => onAvatarChange(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
          </label>
        ) : null}

        {error ? <p className="form-error">{error}</p> : null}
        {saved ? <p className="form-hint">Saved.</p> : null}

        <button className="button primary" type="submit" disabled={busy}>
          Save changes
        </button>
      </form>

      <ChangePasswordForm onChanged={refresh} />
    </section>
  );
}

function ChangePasswordForm({ onChanged }: { onChanged: () => void }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await changeMyPassword({ currentPassword, newPassword });
      setCurrentPassword("");
      setNewPassword("");
      setSaved(true);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to change password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="form-section">
      <h2>Change password</h2>
      <label>
        Current password
        <input
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          autoComplete="current-password"
          required
          disabled={busy}
        />
      </label>
      <label>
        New password
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          autoComplete="new-password"
          minLength={10}
          required
          disabled={busy}
        />
      </label>

      {error ? <p className="form-error">{error}</p> : null}
      {saved ? <p className="form-hint">Password changed.</p> : null}

      <button className="button primary" type="submit" disabled={busy}>
        Change password
      </button>
    </form>
  );
}
